import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { InlineError } from '../InlineError'
import PoHeaderDialog, { type HeaderDialogMode } from './PoHeaderDialog'
import { broadcast } from './sync'
import type { MultiStore, PoListRow } from './LoadPos'

// The PO Loader (docs/BossMultiPO.md §4.1): cascading columns Type → Supplier → Season → Store →
// PO, with quick find to jump straight to a brand and season. Any number of POs on one supplier
// and season load together; a Direct PO of a store outside the user's allowed stores loads
// read-only. On a Group PO the Store column picks whose share is edited.

export type PoType = 'direct' | 'group'

/** Where the Loader is pointed; kept in the URL so a refresh or Back lands in the same place. */
export type LoaderState = { type: PoType; supplier: string | null; season: string | null; ownerStore: string | null }

const NO_SEASON = 'No season'
const seasonOf = (r: PoListRow) => r.season?.trim() || NO_SEASON
const typeOf = (r: PoListRow): PoType => (r.isGroup ? 'group' : 'direct')
const fmtDate = (s: string | null) => (s ? s.slice(0, 10) : '—')
const same = (a: string | null | undefined, b: string | null | undefined) => (a ?? '').toLowerCase() === (b ?? '').toLowerCase()

/** Counts per key, in first-seen order. */
function tally<T>(rows: T[], key: (r: T) => string): [string, number][] {
    const map = new Map<string, number>()
    for (const r of rows) map.set(key(r), (map.get(key(r)) ?? 0) + 1)
    return [...map.entries()]
}

export type QuickFindHit =
    | { kind: 'brand'; supplier: string; season: string; type: PoType; count: number }
    | { kind: 'po'; row: PoListRow }

/**
 * Quick find: every word typed must appear in the brand and season (or, for a PO, its label,
 * brand, season or store). Brand/season pairs come first, then single POs.
 */
export function quickFind(rows: PoListRow[], text: string, limit = 8): QuickFindHit[] {
    const words = text.toLowerCase().split(/\s+/).filter(Boolean)
    if (words.length === 0) return []
    const matches = (hay: string) => words.every(w => hay.includes(w))

    const brands = new Map<string, QuickFindHit & { kind: 'brand' }>()
    for (const r of rows) {
        if (!matches(`${r.supplierName} ${seasonOf(r)}`.toLowerCase())) continue
        const k = `${r.supplierName}|${seasonOf(r)}`
        const hit = brands.get(k)
        if (hit) {
            hit.count++
            // Prefer Direct when the pair has both, as most store work is on Direct POs.
            if (r.isGroup === false) hit.type = 'direct'
        } else {
            brands.set(k, { kind: 'brand', supplier: r.supplierName, season: seasonOf(r), type: typeOf(r), count: 1 })
        }
    }
    const pos: QuickFindHit[] = rows
        .filter(r => matches(`${r.label} ${r.poNumber} ${r.supplierName} ${seasonOf(r)} ${r.storeName ?? ''}`.toLowerCase()))
        .slice(0, limit)
        .map(row => ({ kind: 'po', row }))
    return [...[...brands.values()].slice(0, limit), ...pos].slice(0, limit * 2)
}

export default function PoLoader({ token, store, stores, state, writesEnabled, onState, onActingStore, onLoad }: {
    token: string | null
    /** The store the user is working as: on-hand and Group PO shares are this store's. */
    store: string
    stores: MultiStore[]
    state: LoaderState
    writesEnabled: boolean
    onState: (s: LoaderState) => void
    onActingStore: (storeId: string) => void
    onLoad: (guids: string[]) => void
}) {
    const queryClient = useQueryClient()
    const [selected, setSelected] = useState<string[]>([])
    const [dialog, setDialog] = useState<HeaderDialogMode | null>(null)
    const [hideLocked, setHideLocked] = useState(false)

    const listOptions = { queryKey: ['multipo-list'], queryFn: (): Promise<PoListRow[]> => api('/api/purchase-orders/multi/list', token) }
    const listQuery = useQuery<PoListRow[]>(listOptions)
    const rows = useMemo(() => listQuery.data ?? [], [listQuery.data])

    // ── The columns, each narrowed by the picks to its left ──
    const ofType = useMemo(() => rows.filter(r => typeOf(r) === state.type), [rows, state.type])
    const suppliers = useMemo(() => tally(ofType, r => r.supplierName).sort((a, b) => a[0].localeCompare(b[0])), [ofType])
    const ofSupplier = useMemo(() => ofType.filter(r => r.supplierName === state.supplier), [ofType, state.supplier])
    // Newest season first: legacy seasons are codes like FW26 / SS27, so sort on the year.
    const seasons = useMemo(() => tally(ofSupplier, seasonOf).sort((a, b) => seasonSortKey(b[0]).localeCompare(seasonSortKey(a[0]))), [ofSupplier])
    const ofSeason = useMemo(() => ofSupplier.filter(r => seasonOf(r) === state.season), [ofSupplier, state.season])
    const mayAct = (storeId: string) => stores.some(s => same(s.storeId, storeId))

    // Direct: the stores that own a PO here. Group: the user's stores, to pick whose share to edit.
    const storeEntries = useMemo(() => {
        if (state.type === 'group')
            return stores.map(s => ({ id: s.storeId, label: `${s.storeNumber ?? ''} ${s.name}`.trim(), count: null as number | null, readOnly: false }))
        const owners = new Map<string, { id: string; label: string; count: number; readOnly: boolean; num: string }>()
        for (const r of ofSeason) {
            if (!r.storeGuid) continue
            const id = r.storeGuid.toLowerCase()
            const entry = owners.get(id) ?? { id, label: `${r.storeNumber ?? ''} ${r.storeName ?? ''}`.trim(), count: 0, readOnly: !!r.readOnly, num: r.storeNumber ?? '' }
            entry.count++
            owners.set(id, entry)
        }
        return [...owners.values()].sort((a, b) => a.num.localeCompare(b.num, undefined, { numeric: true }))
    }, [state.type, stores, ofSeason])

    const poRows = useMemo(() => ofSeason
        .filter(r => state.type === 'group' || !state.ownerStore || same(r.storeGuid, state.ownerStore))
        .filter(r => !hideLocked || !r.locked || selected.includes(r.poGuid)), [ofSeason, state.type, state.ownerStore, hideLocked, selected])

    // A selection belongs to one supplier and season; moving to another starts a new one.
    const selectionKey = `${state.supplier}|${state.season}`
    const lastKey = useRef(selectionKey)
    /** A PO to select once the Loader has moved to its supplier and season (quick find, a new PO). */
    const pendingPo = useRef<string | null>(null)
    useEffect(() => {
        if (lastKey.current !== selectionKey) {
            setSelected(pendingPo.current ? [pendingPo.current] : [])
            pendingPo.current = null
        }
        lastKey.current = selectionKey
    }, [selectionKey])

    // POs loaded together must share a catalog (legacy rule); a supplier and season is nearly always one.
    const catalog = rows.find(r => r.poGuid === selected[0])?.catalogGuid ?? null
    const toggle = (r: PoListRow) =>
        setSelected(s => s.includes(r.poGuid) ? s.filter(g => g !== r.poGuid) : [...s, r.poGuid])

    const pick = (changes: Partial<LoaderState>) => onState({ ...state, ...changes })
    const pickStore = (id: string | null) => {
        pick({ ownerStore: state.type === 'direct' ? id : null })
        // Picking one of the user's own stores also makes it the store they work as.
        if (id && mayAct(id) && !same(id, store)) onActingStore(stores.find(s => same(s.storeId, id))!.storeId)
    }

    const jump = (hit: QuickFindHit) => {
        if (hit.kind === 'brand') {
            onState({ type: hit.type, supplier: hit.supplier, season: hit.season, ownerStore: null })
            return
        }
        const r = hit.row
        if (`${r.supplierName}|${seasonOf(r)}` === selectionKey) setSelected(s => s.includes(r.poGuid) ? s : [...s, r.poGuid])
        else pendingPo.current = r.poGuid
        onState({ type: typeOf(r), supplier: r.supplierName, season: seasonOf(r), ownerStore: r.isGroup ? null : r.storeGuid?.toLowerCase() ?? null })
    }

    const selectedRows = rows.filter(r => selected.includes(r.poGuid))
    const typeCounts = { direct: rows.filter(r => !r.isGroup).length, group: rows.filter(r => r.isGroup).length }

    return (
        <div className="po-panel multipo-loader">
            <QuickFind rows={rows} onPick={jump} />

            {listQuery.isError && <InlineError message="Failed to load the PO list from the legacy database." />}
            {listQuery.isLoading && <div className="empty-state"><p>Loading POs…</p></div>}

            {!listQuery.isLoading && (
                <div className="multipo-cols">
                    <Column title="Type">
                        {(['direct', 'group'] as PoType[]).map(t => (
                            <ColumnItem key={t} selected={state.type === t} count={typeCounts[t]}
                                onClick={() => pick({ type: t, ownerStore: null })}>
                                {t === 'direct' ? 'Direct' : 'Group'}
                            </ColumnItem>
                        ))}
                    </Column>
                    <Column title="Supplier">
                        {suppliers.map(([name, n]) => (
                            <ColumnItem key={name} selected={state.supplier === name} count={n}
                                onClick={() => pick({ supplier: name, season: null, ownerStore: null })}>{name}</ColumnItem>
                        ))}
                        {suppliers.length === 0 && <div className="multipo-col-empty">No {state.type} POs.</div>}
                    </Column>
                    <Column title="Season">
                        {seasons.map(([season, n]) => (
                            <ColumnItem key={season} selected={state.season === season} count={n}
                                onClick={() => pick({ season, ownerStore: null })}>{season}</ColumnItem>
                        ))}
                        {!state.supplier && <div className="multipo-col-empty">Pick a supplier.</div>}
                    </Column>
                    <Column title={state.type === 'group' ? 'Store (whose share)' : 'Store'}>
                        {state.season && state.type === 'direct' && (
                            <ColumnItem selected={!state.ownerStore} count={ofSeason.length} onClick={() => pickStore(null)}>All stores</ColumnItem>
                        )}
                        {state.season && storeEntries.map(s => {
                            const isSelected = state.type === 'group' ? same(s.id, store) : same(s.id, state.ownerStore)
                            return (
                                <ColumnItem key={s.id} selected={isSelected} count={s.count} readOnly={s.readOnly}
                                    title={s.readOnly ? 'Not one of your stores: its POs open read-only.' : undefined}
                                    onClick={() => pickStore(s.id)}>
                                    {s.label}{s.readOnly ? ' 🔒' : ''}
                                </ColumnItem>
                            )
                        })}
                        {!state.season && <div className="multipo-col-empty">Pick a season.</div>}
                    </Column>
                    <Column title="PO" wide action={state.season ? (
                        <label className="multipo-check">
                            <input type="checkbox" checked={hideLocked} onChange={e => setHideLocked(e.target.checked)} />
                            Hide locked
                        </label>
                    ) : undefined}>
                        {poRows.map(r => {
                            const checked = selected.includes(r.poGuid)
                            const otherCatalog = catalog !== null && r.catalogGuid !== catalog
                            return (
                                <button key={r.poGuid} type="button"
                                    className={`multipo-col-item multipo-po-item ${checked ? 'selected' : ''} ${r.readOnly ? 'readonly' : ''}`}
                                    disabled={otherCatalog}
                                    title={otherCatalog ? 'Only POs on the same catalog can be loaded together.' : r.locked ? r.lockReason : undefined}
                                    onClick={() => toggle(r)}>
                                    <input type="checkbox" checked={checked} readOnly tabIndex={-1} />
                                    <span className="multipo-po-main">
                                        <strong>{r.label}</strong>
                                        <span className="multipo-po-meta">
                                            <span className="multipo-nowrap">Ship {fmtDate(r.shipDate)}</span>
                                            {r.isGroup && r.preShipLockdownDate && <span className="multipo-nowrap"> · lockdown {fmtDate(r.preShipLockdownDate)}</span>}
                                            {r.locked && <span className="multipo-lock"> · 🔒 {r.readOnly ? 'read-only' : r.lockReason}</span>}
                                        </span>
                                    </span>
                                    <span className="multipo-col-count">{r.units ? `${r.units} u` : ''}</span>
                                </button>
                            )
                        })}
                        {state.season && poRows.length === 0 && <div className="multipo-col-empty">No POs match.</div>}
                        {!state.season && <div className="multipo-col-empty">Pick a season to see its POs.</div>}
                    </Column>
                </div>
            )}

            <div className="multipo-loader-bar">
                <span className="multipo-loader-summary">
                    {selected.length > 0
                        ? <>{selected.length} selected · {state.supplier} {state.season}
                            {selectedRows.some(r => r.readOnly) && ' · some open read-only'}</>
                        : 'Tick the POs to load. You can load any number on one supplier and season.'}
                </span>
                {writesEnabled && (
                    <>
                        <button className="btn btn-secondary" onClick={() => setDialog({ kind: 'new' })}>New PO</button>
                        <button className="btn btn-secondary" disabled={selected.length !== 1}
                            title="Copy the selected PO's quantities into a new PO"
                            onClick={() => setDialog({ kind: 'copy', from: selected[0] })}>Copy PO</button>
                        <button className="btn btn-secondary" disabled={selected.length !== 1 || !!selectedRows[0]?.readOnly}
                            onClick={() => setDialog({ kind: 'edit', poGuid: selected[0] })}>Edit PO</button>
                    </>
                )}
                <button className="btn btn-primary" disabled={selected.length === 0} onClick={() => onLoad(selected)}>
                    Load {selected.length > 0 ? `${selected.length} PO${selected.length > 1 ? 's' : ''}` : 'selected'}
                </button>
            </div>

            {dialog && (
                <PoHeaderDialog token={token} store={store} mode={dialog} onClose={() => setDialog(null)}
                    onSaved={poGuid => {
                        setDialog(null)
                        queryClient.invalidateQueries({ queryKey: ['multipo-load'] })
                        broadcast({ type: 'po-changed', poGuid })
                        // A new or changed PO arrives selected, ready to load, once the list has it.
                        queryClient.fetchQuery({ ...listOptions, staleTime: 0 }).then(list => {
                            const row = list.find(r => same(r.poGuid, poGuid))
                            if (row) jump({ kind: 'po', row })
                        }).catch(() => { /* the list shows its own error */ })
                    }} />
            )}
        </div>
    )
}

/**
 * Sorts season codes by year, then spring/summer before fall/winter: "SS26" → "0026 1",
 * "FW26" → "0026 2". Codes without a year sort as written.
 */
export function seasonSortKey(season: string): string {
    const m = season.trim().match(/^(\D*)(\d{2,4})(.*)$/)
    if (!m) return season
    const half = /^s/i.test(m[1]) ? '1' : '2'
    return `${m[2].padStart(4, '0')} ${half}${m[1]}${m[3]}`
}

function Column({ title, wide, action, children }: { title: string; wide?: boolean; action?: React.ReactNode; children: React.ReactNode }) {
    return (
        <div className={`multipo-col ${wide ? 'wide' : ''}`}>
            <div className="multipo-col-head"><span>{title}</span>{action}</div>
            <div className="multipo-col-body">{children}</div>
        </div>
    )
}

function ColumnItem({ selected, count, readOnly, title, onClick, children }: {
    selected: boolean; count: number | null; readOnly?: boolean; title?: string; onClick: () => void; children: React.ReactNode
}) {
    return (
        <button type="button" className={`multipo-col-item ${selected ? 'selected' : ''} ${readOnly ? 'readonly' : ''}`}
            title={title} onClick={onClick}>
            <span className="multipo-col-label">{children}</span>
            {count !== null && <span className="multipo-col-count">{count}</span>}
        </button>
    )
}

function QuickFind({ rows, onPick }: { rows: PoListRow[]; onPick: (hit: QuickFindHit) => void }) {
    const [text, setText] = useState('')
    const [open, setOpen] = useState(false)
    const [active, setActive] = useState(0)
    const hits = useMemo(() => quickFind(rows, text), [rows, text])
    useEffect(() => setActive(0), [text])

    const choose = (hit: QuickFindHit | undefined) => {
        if (!hit) return
        onPick(hit)
        setText('')
        setOpen(false)
    }

    return (
        <div className="multipo-quickfind">
            <input className="form-input" placeholder="Quick find: brand, season or PO (e.g. “pata fw26”)" value={text}
                aria-label="Quick find" role="combobox" aria-expanded={open && hits.length > 0}
                onChange={e => { setText(e.target.value); setOpen(true) }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                onKeyDown={e => {
                    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, hits.length - 1)) }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)) }
                    else if (e.key === 'Enter') { e.preventDefault(); choose(hits[active]) }
                    else if (e.key === 'Escape') { setText(''); setOpen(false) }
                }} />
            {open && text.trim() && (
                <div className="multipo-qf-results" role="listbox">
                    {hits.length === 0 && <div className="multipo-col-empty">Nothing matches “{text.trim()}”.</div>}
                    {hits.map((h, i) => (
                        <button key={h.kind === 'brand' ? `b|${h.supplier}|${h.season}` : `p|${h.row.poGuid}`} type="button"
                            role="option" aria-selected={i === active}
                            className={`multipo-qf-item ${i === active ? 'active' : ''}`}
                            onMouseDown={e => e.preventDefault()} onMouseEnter={() => setActive(i)} onClick={() => choose(h)}>
                            {h.kind === 'brand'
                                ? <><span><strong>{h.supplier}</strong> · {h.season}</span><span className="multipo-col-count">{h.count} PO{h.count > 1 ? 's' : ''}</span></>
                                : <><span>{h.row.label} <span className="multipo-po-meta">{h.row.supplierName} · {seasonOf(h.row)}{h.row.isGroup ? ' · Group' : ''}</span></span>
                                    <span className="multipo-col-count">{h.row.readOnly ? '🔒 read-only' : 'PO'}</span></>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
